import React, { useState, useRef, useCallback } from 'react';
import { ImageOff, Film, Image, Play } from 'lucide-react';
import { ContentType } from '../../types/content';
import { cn } from '../../utils/helpers';

interface ContentMediaPreviewProps {
  type: ContentType;
  videoUrl: string | null;
  posterUrl: string | null;
  width: number | null;
  height: number | null;
  className?: string;
}

const ContentMediaPreview: React.FC<ContentMediaPreviewProps> = ({
  type,
  videoUrl,
  posterUrl,
  width,
  height,
  className,
}) => {
  const [error, setError] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Reset error state when URLs change
  const prevUrlRef = useRef(videoUrl);
  if (prevUrlRef.current !== videoUrl) {
    prevUrlRef.current = videoUrl;
    if (error) setError(false);
    if (videoReady) setVideoReady(false);
  }

  const handlePlay = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.play().catch(() => {});
    }
  }, []);

  if (error || (!videoUrl && !posterUrl)) {
    return (
      <div
        className={cn(
          'flex flex-col items-center justify-center bg-muted/30 rounded-2xl border border-border min-h-[300px]',
          className
        )}
      >
        <ImageOff className="h-10 w-10 text-muted-foreground/50 mb-2" />
        <span className="text-sm text-muted-foreground">Preview unavailable</span>
      </div>
    );
  }

  // Phone-like frame: centered, max width constrained, full height visible
  const isVertical = width && height ? height > width : true;

  if (type === ContentType.VIDEO && videoUrl) {
    return (
      <div className={cn('flex justify-center', className)}>
        <div
          className={cn(
            'relative rounded-2xl overflow-hidden bg-black border-2 border-border/50',
            isVertical ? 'w-full max-w-[320px]' : 'w-full max-w-[560px]'
          )}
        >
          <video
            ref={videoRef}
            key={videoUrl}
            src={videoUrl}
            poster={posterUrl || undefined}
            controls
            playsInline
            preload="auto"
            className="w-full h-auto block"
            onCanPlay={() => setVideoReady(true)}
            onError={() => setError(true)}
          >
            Your browser does not support the video tag.
          </video>

          {/* Play overlay when not yet interacted */}
          {!videoReady && posterUrl && (
            <div
              className="absolute inset-0 flex items-center justify-center cursor-pointer bg-black/20"
              onClick={handlePlay}
            >
              <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
                <Play className="h-6 w-6 text-black ml-0.5" />
              </div>
            </div>
          )}

          {/* Type badge */}
          <div className="absolute top-3 left-3 bg-black/60 text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
            <Film className="h-3 w-3" />
            Video
          </div>
        </div>
      </div>
    );
  }

  // PHOTO or fallback to poster image
  const imageUrl = type === ContentType.PHOTO ? (videoUrl || posterUrl) : posterUrl;

  return (
    <div className={cn('flex justify-center', className)}>
      <div
        className={cn(
          'relative rounded-2xl overflow-hidden bg-black border-2 border-border/50',
          isVertical ? 'w-full max-w-[320px]' : 'w-full max-w-[560px]'
        )}
      >
        <img
          src={imageUrl || ''}
          alt="Content preview"
          className="w-full h-auto block"
          onError={() => setError(true)}
        />
        <div className="absolute top-3 left-3 bg-black/60 text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
          <Image className="h-3 w-3" />
          Photo
        </div>
      </div>
    </div>
  );
};

export default ContentMediaPreview;
